import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
    assign,
    filter,
    find,
    get,
    getStorePluginUrl,
    has,
    isMessageFromTargetIFrame,
    isEmpty,
    map,
    omit,
    reduce,
    textLimit,
    tracker
} from '~/utils';
import Intl from '@fx-ui/intl';
import { Button, Check, Drawer, Message } from '@fx-ui/fine-design';
import { POST_MESSAGE_KEY, TRACKER } from '~/constants';

export interface IInstallPlugin {
    _id: string;
    name: string;
    icon: string;
    description?: string;
    [key: string]: any;
}

export interface IPluginInstallRef {
    getValue: () => Record<string, IInstallPlugin>;
}

/**
 * 插件安装列表
 *
 * @param props
 * @param ref
 * @class
 */
const PluginInstall: React.ForwardRefRenderFunction<
    IPluginInstallRef,
    {
        plugins: IInstallPlugin[];
        /** 正在安装的模版 app_id，用于埋点 p4：fa-{appId} */
        appIds: string[];
    }
> = (props, ref) => {
    const { plugins, appIds } = props;
    const pluginStoreSource = map(appIds, (appId) => `fa-${appId}`).join('/');
    const [pluginList, setPluginList] = useState<IInstallPlugin[]>(plugins);
    const [pluginChecked, setPluginChecked] = useState<Record<string, IInstallPlugin>>({});
    const [detailPluginId, setDetailPluginId] = useState<string>();
    const pluginListRef = useRef<IInstallPlugin[]>(plugins);
    const iframeRef = useRef<HTMLIFrameElement>(null);
    useImperativeHandle(ref, () => ({
        getValue
    }));

    useEffect(() => {
        pluginListRef.current = plugins;
        setPluginList(plugins);
        // 默认全选
        if (!isEmpty(plugins)) {
            setPluginChecked(
                reduce(
                    plugins,
                    (result: Record<string, IInstallPlugin>, plugin) => {
                        assign(result, {
                            [plugin._id]: plugin
                        });
                        return result;
                    },
                    {}
                )
            );
        }
    }, [plugins]);

    useEffect(() => {
        const handleMessage = (event: MessageEvent) => {
            if (!isMessageFromTargetIFrame(event, iframeRef.current?.contentWindow)) {
                return;
            }
            if (get(event, 'data.type') !== POST_MESSAGE_KEY.PLUGIN_INSTALL_AGENT) {
                return;
            }
            const pluginAgent = get(event, 'data.data');
            const pluginId = get(pluginAgent, 'plugin_id');
            if (!pluginId || !get(pluginAgent, 'is_installed')) {
                return;
            }
            const installedPlugin = find(
                pluginListRef.current,
                (plugin) => plugin._id === pluginId
            );
            if (!installedPlugin) {
                return;
            }
            setDetailPluginId(undefined);
            const nextPluginList = filter(
                pluginListRef.current,
                (plugin) => plugin._id !== pluginId
            );
            pluginListRef.current = nextPluginList;
            setPluginList(nextPluginList);
            setPluginChecked((prev) => omit(prev, pluginId));
            Message.add({
                messageType: 'toast',
                type: 'success',
                message: Intl.get('plugin.install.success.name', {
                    name: textLimit(get(pluginAgent, 'name') || installedPlugin.name, 12)
                })
            });
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    const togglePluginChecked = (plugin: IInstallPlugin) => {
        setPluginChecked((prev) => {
            if (has(prev, plugin._id)) {
                tracker(TRACKER.PLUGIN_INSTALL_CHECK_DISABLE, plugin.name);
                return omit(prev, plugin._id);
            } else {
                return assign({}, prev, {
                    [plugin._id]: plugin
                });
            }
        });
    };

    const detailPlugin = find(pluginList, (plugin) => plugin._id === detailPluginId);

    const openPluginDetail = (plugin: IInstallPlugin) => {
        setDetailPluginId(plugin._id);
        tracker(TRACKER.PLUGIN_DETAIL, {
            p1: plugin.name,
            p2: '',
            p3: plugin._id,
            p4: pluginStoreSource
        });
    };

    const getValue = () => {
        return pluginChecked;
    };
    return (
        <div className="plugin-content">
            <div className="plugin-text">{Intl.get('app.plugin.install.tip')}</div>
            <div className="plugin-container">
                {map(pluginList, (v) => {
                    if (!v) {
                        return null;
                    }
                    return (
                        <div className="plugin-bar" key={v._id}>
                            <div className="bar-left">
                                <img alt="" src={v.icon} />
                            </div>
                            <div className="bar-mid">
                                <div className="bar-mid-title">{v.name}</div>
                                <div className="bar-mid-desc">{v.description}</div>
                                <Button
                                    className="bar-detail-link"
                                    compact
                                    type="text"
                                    onClick={() => {
                                        openPluginDetail(v);
                                    }}
                                >
                                    {Intl.get('view.detail')}
                                </Button>
                            </div>
                            <div
                                className="bar-right"
                                onClick={() => {
                                    togglePluginChecked(v);
                                }}
                            >
                                <Check checked={has(pluginChecked, v._id)} />
                            </div>
                        </div>
                    );
                })}
            </div>
            <Drawer
                className="fx-plugin-install-detail-drawer"
                visible={!!detailPluginId}
                onClose={() => setDetailPluginId(undefined)}
                title={detailPlugin?.name}
                autoClose
            >
                {detailPluginId && (
                    <iframe
                        ref={iframeRef}
                        className="plugin-detail-iframe"
                        src={getStorePluginUrl(detailPluginId, pluginStoreSource)}
                    />
                )}
            </Drawer>
        </div>
    );
};

export default forwardRef(PluginInstall);
